import {FC, useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import Button from '@components/Button';
import {billingPayments} from './billingPayments.style';
import {ReactComponent as StripeIcon} from '@/assets/icons/stripe-logo.svg';
import {ReactComponent as SquareIcon} from '@/assets/icons/square-logo.svg';
import {ReactComponent as PaypalIcon} from '@/assets/icons/paypal-logo.svg';
import {ReactComponent as StripeIconDark} from '@/assets/icons/stripe-logo-dark.svg';
import {ReactComponent as SquareIconDark} from '@/assets/icons/square-logo-dark.svg';
import {ReactComponent as PaypalIconDark} from '@/assets/icons/paypal-logo-dark.svg';
import {useAppSelector} from '@/store/hooks';
import {getOrganizationIdSelector} from '@/store/organization';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';

const BillingPayment: FC = () => {
	const organizationId = useAppSelector(getOrganizationIdSelector);

	const [isStripeConnected, setIsStripeConnected] = useState<boolean>(false);
	const [isStripeEnabled, setIsStripeEnabled] = useState<boolean>(false);
	const [isSquareConnected, setIsSquareConnected] = useState<boolean>(false);
	const [isPaypalConnected, setIsPaypalConnected] = useState<boolean>(false);
	const [isStripeLoading, setIsStripeLoading] = useState<boolean>(false);

	const stripeTitle =  isStripeLoading
    ? 'Connecting...'
    : isStripeConnected && isStripeEnabled
    ? 'Disconnect Stripe'
    : isStripeConnected && !isStripeEnabled
    ? 'Enable Stripe'
    : 'Connect Stripe'

	const stripeDescription = isStripeConnected
	? isStripeEnabled
	  ? 'Your Stripe account is connected.'
	  : 'Your Stripe account is connected but not enabled.'
	: 'Connect your Stripe account.';

	const getStripeSettings = async () => {
		if (!organizationId) return;

		try {
			const response = await OrganizationStripeService.getOrganizationStripePaymentSettings(organizationId);
			const data = response.data;

			const isConnected = !!(data.stripeDetailsSubmitted && data.stripeChargesEnabled && data.stripeConnectedAccountId);

			setIsStripeEnabled(data.stripeEnabled);
			setIsStripeConnected(isConnected);
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const startStripeOnboarding = async () => {
		try {
			setIsStripeLoading(true);
			const toastId = toast.loading('Preparing Stripe onboarding...');
			
        const data={
        isRegistationFlow: false
		}
			const response = await OrganizationStripeService.createStrapiOnboardingLink(organizationId!, data);

			if (response.data?.url) {
				toast.dismiss(toastId);
				toast.success('Redirecting to Stripe...');
				window.location.href = response.data.url;
			} else {
				throw new Error('Onboarding URL not found in response');
			}
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsStripeLoading(false);
		}
	};

	const disableStripeConnection = async () => {
		if (!organizationId) return;
		try {
			setIsStripeLoading(true);
			await OrganizationStripeService.disableOrganizationStripeConnection(organizationId);
			await getStripeSettings();
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsStripeLoading(false);
		}
	};

	const enableStripeConnection = async () => {
		if (!organizationId) return;
		try {
			setIsStripeLoading(true);
			await OrganizationStripeService.enableOrganizationStripeConnection(organizationId);
			await getStripeSettings();
		} catch (error) {
			toast.error(getErrorMessage(error));
		} finally {
			setIsStripeLoading(false);
		}
	};
	const handleStripe = async () => {
		if (!organizationId || isStripeLoading) return;

		if (isStripeConnected && isStripeEnabled) {
			await disableStripeConnection();
		} else if (isStripeConnected && !isStripeEnabled) {
			await enableStripeConnection();
		} else {
			await startStripeOnboarding();
		}
	};

	const handleSquare = () => {
		// TODO Add square connect handler
	};

	const handlePaypal = () => {
		// TODO Add paypal connect handler
	};

	useEffect(() => {
		if (organizationId) {
			getStripeSettings();
		}
	}, [organizationId]);

	useEffect(() => {
		if (!organizationId) return;

		const params = new URLSearchParams(window.location.search);

			const cleanUrlParams = () => {
			const url = new URL(window.location.href);
			url.searchParams.delete('stripeReturn');
			url.searchParams.delete('restartStripe');
			window.history.replaceState({}, document.title, url.pathname + url.search);
		};

		if (params.get('stripeReturn') === '1') {
			const syncStripe = async () => {
				try {
					setIsStripeLoading(true);
					await OrganizationStripeService.syncOrganizationStripeConnection(organizationId);
					await getStripeSettings();
					toast.success('Stripe connected successfully!');
					cleanUrlParams();
				} catch (error) {
					toast.error(getErrorMessage(error));
				} finally {
					setIsStripeLoading(false);
				}
			};
			syncStripe();
		} else if (params.get('restartStripe') === '1') {
			cleanUrlParams();
			startStripeOnboarding();
		}
	}, [organizationId]);

	return (
		<Box sx={billingPayments.container}>
			{/* Stripe Item */}
			<Box sx={billingPayments.paymentItem}>
				<Box sx={billingPayments.paymentHead}>
					<Box sx={billingPayments.iconWrap}>
						<StripeIconDark />
					</Box>
					<Typography sx={billingPayments.title}>Stripe</Typography>
				</Box>
				<Typography sx={billingPayments.description}>
					{stripeDescription}
				</Typography>
				<Button
					onClick={handleStripe}
					title={stripeTitle}
					disabled={isStripeLoading}
					color={isStripeConnected && isStripeEnabled ? 'success' : 'primary'}
					startIcon={<StripeIcon />}
					sx={[billingPayments.btn, billingPayments.stripe]}
				/>
			</Box>

			{/* Square Item */}
			{/* <Box sx={billingPayments.paymentItem}>
				<Box sx={billingPayments.paymentHead}>
					<Box sx={billingPayments.iconWrap}>
						<SquareIconDark />
					</Box>
					<Typography sx={billingPayments.title}>Square</Typography>
				</Box>
				<Typography sx={billingPayments.description}>Connect your Square account. </Typography>
				<Button
					onClick={handleSquare}
					title="Connect with Square"
					color="primary"
					startIcon={<SquareIcon />}
					sx={[billingPayments.btn, billingPayments.square]}
				/>
			</Box> */}

			{/* Paypal Item */}
			{/* <Box sx={billingPayments.paymentItem}>
				<Box sx={billingPayments.paymentHead}>
					<Box sx={billingPayments.iconWrap}>
						<PaypalIconDark />
					</Box>
					<Typography sx={billingPayments.title}>Paypal</Typography>
				</Box>
				<Typography sx={billingPayments.description}>Connect your Paypal account.</Typography>
				<Button
					onClick={handlePaypal}
					title="Connect with Paypal"
					color="primary"
					startIcon={<PaypalIcon />}
					sx={[billingPayments.btn, billingPayments.paypal]}
				/>
			</Box> */}
		</Box>
	);
};

export default BillingPayment;
